/**
 * GenerateImageButton — honest image-generation affordance for the Content
 * Engine workspace.
 *
 * What it does: builds a deterministic per-type prompt (pure helper in
 * lib/content/studioHome), lets the operator edit it, and POSTs it to the
 * `/generate-image` endpoint. The BACKEND IS WIRED SEPARATELY by the
 * operator — this component says so plainly, and when the endpoint isn't
 * there yet it reports exactly that instead of faking an image. When the
 * endpoint responds with `{ url }` (or `{ image }` as a data URI), the
 * image renders inline with a download-friendly link.
 */

import { useEffect, useState } from 'react';
import { Image as ImageIcon, Loader2, AlertTriangle, RefreshCcw } from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import type { ContentItemType } from '../lib/db/types';
import { IMAGE_GEN_ENDPOINT, buildImagePrompt } from '../lib/content/studioHome';

type GenState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'done'; url: string }
  | { status: 'error'; message: string };

export function GenerateImageButton({
  type,
  title,
}: {
  type: ContentItemType;
  /** Title of the piece the image accompanies — seeds the prompt. */
  title: string;
}) {
  const [prompt, setPrompt] = useState(() => buildImagePrompt(type, title));
  const [state, setState] = useState<GenState>({ status: 'idle' });

  // Re-seed the prompt when the workspace switches type/title — but never
  // stomp an in-flight or completed run's edited prompt mid-typing: only
  // reseed when the operator hasn't diverged from a generated seed.
  useEffect(() => {
    setPrompt(buildImagePrompt(type, title));
    setState({ status: 'idle' });
  }, [type, title]);

  const generate = async () => {
    const body = prompt.trim();
    if (!body || state.status === 'loading') return;
    setState({ status: 'loading' });
    try {
      const res = await fetch(IMAGE_GEN_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: body, type }),
      });
      if (!res.ok) {
        throw new Error(
          res.status === 404
            ? `The ${IMAGE_GEN_ENDPOINT} endpoint isn't wired yet (404). Once the operator connects the image backend, this button works as-is.`
            : `Image endpoint answered ${res.status}. Nothing was generated.`,
        );
      }
      // SPA hosts often answer unknown paths with the app's HTML + 200 —
      // detect that and say "not wired" instead of choking on JSON.parse.
      const contentType = res.headers.get('content-type') ?? '';
      if (!contentType.includes('application/json')) {
        throw new Error(
          `The ${IMAGE_GEN_ENDPOINT} endpoint isn't wired yet — the server answered with ` +
            `${contentType || 'no content type'} instead of JSON. The image backend is wired separately.`,
        );
      }
      const data: { url?: string; image?: string } = await res.json();
      const url = data.url ?? data.image;
      if (!url) {
        throw new Error(
          'The endpoint answered without a `url` or `image` field — no image to show.',
        );
      }
      setState({ status: 'done', url });
    } catch (err) {
      setState({
        status: 'error',
        message:
          err instanceof Error
            ? err.message
            : `Could not reach ${IMAGE_GEN_ENDPOINT} — the image backend is wired separately.`,
      });
    }
  };

  return (
    <GlassPanel style={{ padding: 16, display: 'grid', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <ImageIcon size={16} style={{ color: C.magentaSoft, flexShrink: 0 }} />
        <span className="t-section-title" style={{ color: C.text }}>
          Generate image
        </span>
        <span
          className="glass-pill"
          title={`POSTs your prompt to ${IMAGE_GEN_ENDPOINT}; the image backend is wired separately by the operator.`}
          style={{
            fontSize: 10,
            padding: '2px 8px',
            color: C.textMuted,
            background: 'rgba(180,188,210,0.12)',
          }}
        >
          backend wired separately
        </span>
      </div>

      <p className="t-meta" style={{ margin: 0, color: C.textDim }}>
        Sends this prompt to <code style={{ fontSize: 10 }}>{IMAGE_GEN_ENDPOINT}</code> and
        renders the returned image. Until the operator wires that endpoint, the button
        reports the miss honestly — it never fakes a picture.
      </p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={2}
          aria-label="Image prompt"
          style={{
            flex: 1,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 8,
            color: C.text,
            fontSize: 12,
            lineHeight: 1.5,
            padding: '8px 10px',
            resize: 'vertical',
          }}
        />
        <button
          onClick={() => void generate()}
          disabled={!prompt.trim() || state.status === 'loading'}
          className="glass-btn glass-btn--primary"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            opacity: !prompt.trim() || state.status === 'loading' ? 0.5 : 1,
          }}
        >
          {state.status === 'loading' ? (
            <Loader2 size={13} className="animate-spin" />
          ) : (
            <ImageIcon size={13} />
          )}
          Generate image
        </button>
      </div>

      {state.status === 'error' && (
        <div
          className="t-meta"
          style={{ color: C.orangeWarm, display: 'flex', gap: 6, alignItems: 'flex-start' }}
        >
          <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{state.message}</span>
          <button
            onClick={() => void generate()}
            className="glass-btn"
            style={{ padding: '2px 8px', fontSize: 11, marginLeft: 'auto', flexShrink: 0 }}
          >
            <RefreshCcw size={10} />
            Retry
          </button>
        </div>
      )}

      {state.status === 'done' && (
        <a href={state.url} target="_blank" rel="noreferrer" style={{ justifySelf: 'start' }}>
          <img
            src={state.url}
            alt={prompt}
            style={{
              maxWidth: '100%',
              maxHeight: 320,
              borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.12)',
              display: 'block',
            }}
          />
        </a>
      )}
    </GlassPanel>
  );
}
